import { IAssemblyApplicationTypeResponse } from '@kcftech/prometheus-client';
import { useMutation } from '@tanstack/react-query';
import {
    SwitchInlineEditor,
    SwitchInlineEditorProps,
} from 'components/inline_edit/ToggleInlineEditor';
import { useNotificationContext } from 'contexts/NotificationContext';
import { usePermissions } from 'contexts/PermissionsContext';
import { useState } from 'react';
import { updateAssemblyType } from 'utils/assembly_application';

export type AssemblyTypeStatusInlineEditorProps = Omit<
    SwitchInlineEditorProps,
    'value' | 'onConfirm' | 'validate'
> &
    Readonly<{
        assemblyType: IAssemblyApplicationTypeResponse;
        onUpdate: () => void;
    }>;

export function AssemblyTypeStatusInlineEditor({
    assemblyType,
    onUpdate,
}: AssemblyTypeStatusInlineEditorProps) {
    const { canEditAssemblyType } = usePermissions();
    const { showToast } = useNotificationContext();
    const [isActive, setIsActive] = useState(assemblyType.Active);

    const { mutate: changeAssemblyTypeStatus, isPending } = useMutation({
        mutationFn: async (updatedStatus: boolean) =>
            updateAssemblyType(assemblyType.Id, {
                Active: updatedStatus,
            }),

        onSuccess: (response) => {
            setIsActive(response.Active);
            onUpdate();
        },

        onError: () => {
            showToast({
                type: 'error',
                content: 'Failed to change assembly type status',
            });
        },
    });

    return (
        <div
            aria-label={`${assemblyType.Name} status switch`}
            title="Edit assembly type status"
        >
            <SwitchInlineEditor
                renderValue={(value) => (value ? 'Active' : 'Inactive')}
                value={isActive}
                isDisabled={!canEditAssemblyType || isPending}
                onConfirm={(val) => changeAssemblyTypeStatus(val ?? false)}
            />
        </div>
    );
}
